/*
 * PD What's Included (GRG-55) — dynamic bundle-contents list.
 *
 * All rows (base + every lens/kit option) are server-rendered by
 * snippets/pd-whats-included.liquid; option rows start hidden. This component
 * only:
 *   1. shows/hides option rows to match the current bundle selection
 *      (the `pd-bundle:selection` event from <pd-bundle-configurator>),
 *   2. keeps the "(N)" count in sync,
 *   3. swaps itself in for the element named by data-swap-target (the plain
 *      Product Overview body) while a selection exists, and back when cleared,
 *   4. lazy-fetches a row's overview the first time its <details> opens
 *      (full presentation only) via /products/{handle}?view={fetch-view},
 *   5. handles the read more / show less toggle inside row panels (delegated —
 *      panels arrive by fetch, and the legacy product-overview-tab JS only
 *      binds the plain overview's button).
 *
 * Expand/collapse and the plus/minus swap are native <details> + CSS — no JS.
 */
if (!customElements.get('pd-whats-included')) {
  customElements.define(
    'pd-whats-included',
    class PdWhatsIncluded extends HTMLElement {
      connectedCallback() {
        this.expandable = this.dataset.expandable === 'true';
        this.fetchView = this.dataset.fetchView || 'pd-overview';
        this.list = this.querySelector('[data-wi-list]');
        this.countEl = this.querySelector('[data-wi-count]');
        this.swapTarget = this.dataset.swapTarget
          ? document.querySelector(this.dataset.swapTarget)
          : null;

        this._onSelection = this.onSelection.bind(this);
        document.addEventListener('pd-bundle:selection', this._onSelection);

        if (this.expandable) {
          // `toggle` doesn't bubble, but the capture phase still reaches it.
          this.addEventListener('toggle', this.onToggle.bind(this), true);
          this.addEventListener('click', this.onPanelReadMore.bind(this));
        }

        // The bundle may have emitted before we connected — read current state.
        this.syncFromBundle();
      }

      disconnectedCallback() {
        document.removeEventListener('pd-bundle:selection', this._onSelection);
      }

      syncFromBundle() {
        const bundle = document.querySelector('pd-bundle-configurator');
        if (bundle && typeof bundle.getSelectionItems === 'function') {
          this.update(bundle.getSelectionItems());
        }
      }

      onSelection(evt) {
        this.update((evt.detail && evt.detail.items) || []);
      }

      // The whole list only appears once a lens or kit is selected. When shown,
      // only the selected option rows (+ the always-present base row) are visible,
      // and the swap target (the plain overview body) is hidden.
      // Visibility uses the `hidden` class (Dawn ships .hidden{display:none!important},
      // which beats the row's flex/block utilities — the `hidden` attribute does not).
      update(items) {
        if (!this.list) return;

        const active = items.length > 0;
        this.classList.toggle('hidden', !active);
        if (this.swapTarget) this.swapTarget.classList.toggle('hidden', active);

        const selected = {};
        items.forEach((item) => {
          selected[item.group] = item.handle;
        });

        this.list.querySelectorAll('[data-wi-row]:not([data-wi-row="base"])').forEach((row) => {
          const show = selected[row.dataset.group] === row.dataset.handle;
          row.classList.toggle('hidden', !show);
        });

        if (this.countEl) this.countEl.textContent = String(1 + items.length);
      }

      onToggle(evt) {
        const details = evt.target;
        if (!details.open || !details.matches('[data-wi-row]')) return;
        const panel = details.querySelector('[data-wi-panel]');
        if (panel && panel.dataset.loaded === 'false') {
          this.loadPanel(details.dataset.handle, panel);
        }
      }

      // Read more / show less inside a row panel. Scoped to [data-wi-panel] so
      // the plain overview's button (owned by the legacy product-overview-tab
      // JS) is never double-handled.
      onPanelReadMore(evt) {
        const button = evt.target.closest('[data-description-button]');
        if (!button || !button.closest('[data-wi-panel]')) return;
        const root = button.closest('[data-overview-content]');
        if (!root) return;
        const short = root.querySelector('[data-description-short]');
        const full = root.querySelector('[data-description-full]');
        if (!short || !full) return;
        const opening = full.classList.contains('hidden');
        short.classList.toggle('hidden', opening);
        full.classList.toggle('hidden', !opening);
        button.textContent = opening
          ? button.dataset.labelLess || 'Show less'
          : button.dataset.labelMore || 'Read More';
      }

      loadPanel(handle, panel) {
        if (!handle) return;
        panel.dataset.loaded = 'pending';
        panel.innerHTML =
          '<div class="pd-whats-included__loading py-4 text-sm text-soft">Loading…</div>';

        fetch('/products/' + encodeURIComponent(handle) + '?view=' + this.fetchView, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        })
          .then((res) => (res.ok ? res.text() : Promise.reject(res.status)))
          .then((html) => {
            panel.innerHTML = html;
            panel.dataset.loaded = 'true';
          })
          .catch(() => {
            panel.dataset.loaded = 'false';
            panel.innerHTML =
              '<div class="pd-whats-included__error py-4 text-sm text-soft">' +
              '<a href="/products/' +
              encodeURIComponent(handle) +
              '">View product details</a></div>';
          });
      }
    }
  );
}
